/*
 * The reconnect policy for long-lived sessions that ride on the web app
 * (terminal/jshell/shell/GUI WebSockets through the runner or machine
 * proxy; plans/zero-downtime-deploys.md Phase 0, the Tier 3 consolation).
 * A deploy or a wifi blip drops the socket; the session itself may be
 * gone (a program run) or not (a per-user machine's shell survives, only
 * the proxy hop died). Either way the pane must not just go dead.
 *
 * Pure (no DOM) so it's unit-testable: the schedule and the give-up point.
 * Delays: 1s, 2s, 4s, 8s, then 15s apart, for MAX_ATTEMPTS tries — a
 * little over two minutes, comfortably longer than a deploy gap and short
 * enough that a really-down server doesn't spin a tab all night.
 */

export const MAX_RECONNECT_ATTEMPTS = 12;

const BASE_MS = 1000;
const CAP_MS = 15_000;

// How long to wait before reconnect attempt number `attempt` (1-based).
export const reconnectDelayMs = (attempt: number): number =>
  Math.min(BASE_MS * 2 ** Math.max(0, attempt - 1), CAP_MS);

// How a session ended, as the socket backend saw it.
export type SessionEnd =
  | 'exit' // the server said so (a clean exit, stop, compile error, refusal)
  | 'dropped' // the socket was open, then closed with no exit message
  | 'unreachable'; // the socket never opened at all

// Whether an ending is the kind reconnecting can fix. A session that ran
// for a while and then dropped is worth one immediate restart (the loop
// counts a quick death as a failure, so a flapping server still backs off).
export const shouldReconnect = (end: SessionEnd): boolean => end !== 'exit';

/*
 * How long a session may go without a keystroke before an ending parks it
 * instead of starting it again. The runner kills a session idle for
 * SERVER_WS_IDLE_SEC (10 min) and the loop used to start a fresh one at
 * once, so a JShell tab left open in a forgotten window restarted every
 * ten minutes forever — each restart a new socket that kept the runner
 * machine up (a machine with a terminal connection is never idle), woke a
 * stopped one through fly's autostart, and counted its owner as present
 * to the fleet manager (modules/runner-fleet.ts). An idle tab now parks
 * with "press any key" and holds nothing. Shorter than the server's limit
 * so a dropped or unreachable socket on an abandoned tab parks too,
 * instead of spending its reconnect attempts; long enough that a student
 * reading the instructions mid-session isn't parked.
 */
export const IDLE_PARK_MS = 5 * 60_000;

// Whether an ended session should wait for a keystroke rather than restart:
// nobody has typed for IDLE_PARK_MS, whatever took the session away.
export const shouldPark = (idleMs: number): boolean => idleMs >= IDLE_PARK_MS;
